import { sendGTMEvent } from "@next/third-parties/google";
import { deviceDetector } from "@ospweb/sdk-ui";
import { getBridge, MessageType } from "../lib/bridge";

/** GA4 单参数值长度上限（约 100 字符），超长会被拒绝 */
const GA4_PARAM_VALUE_MAX_LENGTH = 100;

/**
 * 将单个参数值转为 GA4 可接受的标量；对象/数组序列化后超长则截断。
 */
function toScalarValue(value: unknown): string | number | boolean {
  if (value === null || value === undefined) {
    return "";
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  const str = JSON.stringify(value);
  return str.length > GA4_PARAM_VALUE_MAX_LENGTH
    ? str.slice(0, GA4_PARAM_VALUE_MAX_LENGTH - 3) + "..."
    : str;
}

/**
 * 为 App 内原生上报构建扁平参数。
 * Firebase/GA4 不支持嵌套对象/数组：参数名不能是 "ecommerce"，参数值也不能是对象或数组；
 * purchase 等事件需用顶层扁平参数；items 数组拆成 item_{index}_{key} 扁平字段。
 */
function buildAppLogEventParams(
  data: Record<string, unknown>,
  dataLayerName: string,
): Record<string, unknown> {
  const { event: _event, ecommerce, ...rest } = data;
  let params: Record<string, unknown>;
  if (ecommerce && typeof ecommerce === "object" && !Array.isArray(ecommerce)) {
    params = { ...ecommerce, ...rest, dataLayerName };
  } else {
    params = { ...rest, dataLayerName };
  }
  // items 为数组时拆成扁平字段：item_{index}_{key}，便于 GA4 单参数长度限制且无嵌套
  const items = params.items;
  if (Array.isArray(items)) {
    delete params.items;
    params.items_count = items.length;
    items.forEach((item, index) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        Object.entries(item).forEach(([key, value]) => {
          params[`item_${index}_${key}`] = toScalarValue(value);
        });
      }
    });
  }
  return params;
}

/**
 * 发送事件到 Firebase/GA4
 * @param data 事件数据 『注意：App GA4要求 参数不能是嵌套对象，需要扁平化』
 * @param dataLayerName
 * @returns
 */
export const sendGtmOrApp: typeof sendGTMEvent = (
  data,
  dataLayerName = "dataLayer",
) => {
  if (typeof window === "undefined") {
    return;
  }
  if (deviceDetector.isAndroidApp() || deviceDetector.isIOSApp()) {
    const bridge = getBridge();
    const raw = data as Record<string, unknown>;
    // 必须在这里全部扁平化，否则会被 GA4 拒绝
    const params = buildAppLogEventParams(raw, dataLayerName);
    bridge.send(MessageType.LOG_EVENT, {
      name: String(raw.event),
      params,
    });
  } else {
    sendGTMEvent(data, dataLayerName);
  }
};

// 登录上报单独处理
// 对同一用户多次登录进行去重
export const sendLoginEvent = (ownerId: string, platform?: string) => {
  if (typeof window === "undefined") {
    return;
  }
  const isSent = localStorage.getItem(ownerId);
  if (isSent) {
    return;
  }
  localStorage.setItem(ownerId, "true");
  sendGtmOrApp({
    event: "login",
    method: platform,
    ownerId: ownerId,
  });
};
